/**
 * Sprint 4 §4.5 (2026-06-06) — Voice transcript debug control.
 * 2026-06-08: drag-to-move via a grip handle. Position persisted in
 * localStorage under `briefing-room-voice-debug-position` so the overlay
 * stays where the user put it across reloads. Double-click the handle to
 * reset to default (bottom-right anchor).
 *
 * Fixed overlay (default anchor: bottom-right above the MIND brain widget)
 * with a small textarea + Send button. When the user clicks Send (or presses
 * Ctrl/Cmd+Enter), `onTranscript(text)` fires — the same code path the mic
 * uses. Bypasses the STT layer entirely, which makes it the canonical way
 * for Playwright to drive voice-mode tool calls without mic permission
 * complexity in headless browser contexts.
 *
 * Gated on `atmosphereSettings.voiceDebugEnabled` (default OFF). User opts
 * in via AtmosphereSettingsPanel → Debug → Voice transcript debug control.
 *
 * Testid surface:
 *   - `br-voice-debug-control`        — outer container
 *   - `br-voice-debug-drag-handle`    — drag handle (header strip)
 *   - `br-voice-debug-textarea`       — input textarea
 *   - `br-voice-debug-send`           — submit button
 */
import { useRef, useState } from 'react';
import { useAtmosphereSettings } from '../../hooks/useAtmosphereSettings';
import './VoiceDebugControl.css';

export interface VoiceDebugControlProps {
  /** Called with the textarea contents on Send. Same shape as MicControl's
   *  onTranscript — the consumer (ScenarioRunner) wires this directly to
   *  dispatcher.dispatchTranscript via the existing handleTranscript path. */
  onTranscript: (text: string) => void;
}

const POSITION_KEY = 'briefing-room-voice-debug-position';
const MIN_VISIBLE = 40;

interface Position {
  x: number;
  y: number;
}

function readStoredPosition(): Position | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(POSITION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof (parsed as Position).x === 'number' &&
      typeof (parsed as Position).y === 'number' &&
      Number.isFinite((parsed as Position).x) &&
      Number.isFinite((parsed as Position).y)
    ) {
      return { x: (parsed as Position).x, y: (parsed as Position).y };
    }
  } catch {
    // Corrupt JSON or localStorage unavailable — fall back to default anchor.
  }
  return null;
}

function clampToViewport(x: number, y: number, w: number, h: number): Position {
  const vw = window.innerWidth || 1024;
  const vh = window.innerHeight || 768;
  return {
    x: Math.max(MIN_VISIBLE - w, Math.min(x, vw - MIN_VISIBLE)),
    y: Math.max(0, Math.min(y, vh - MIN_VISIBLE)),
  };
}

export function VoiceDebugControl({ onTranscript }: VoiceDebugControlProps) {
  const { voiceDebugEnabled } = useAtmosphereSettings();
  const [value, setValue] = useState('');
  const [position, setPosition] = useState<Position | null>(() => readStoredPosition());
  const asideRef = useRef<HTMLElement | null>(null);

  if (!voiceDebugEnabled) return null;

  const submit = () => {
    const trimmed = value.trim();
    if (trimmed.length === 0) return;
    onTranscript(trimmed);
    setValue('');
  };

  const onHandleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const aside = asideRef.current;
    if (!aside) return;
    e.preventDefault();
    const rect = aside.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    setPosition({ x: rect.left, y: rect.top });

    const onMove = (ev: MouseEvent) => {
      const w = aside.offsetWidth || 280;
      const h = aside.offsetHeight || 120;
      setPosition(clampToViewport(ev.clientX - offsetX, ev.clientY - offsetY, w, h));
    };
    const onUp = () => {
      setPosition((current) => {
        if (current) {
          try {
            window.localStorage.setItem(POSITION_KEY, JSON.stringify(current));
          } catch {
            // Quota exceeded or unavailable — keep in-memory position only.
          }
        }
        return current;
      });
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const onHandleDoubleClick = () => {
    setPosition(null);
    try {
      window.localStorage.removeItem(POSITION_KEY);
    } catch {
      // ignore
    }
  };

  const inlineStyle: React.CSSProperties | undefined = position
    ? {
        left: `${position.x}px`,
        top: `${position.y}px`,
        right: 'auto',
        bottom: 'auto',
      }
    : undefined;

  return (
    <aside
      ref={asideRef}
      className="br-voice-debug-control"
      data-testid="br-voice-debug-control"
      aria-label="Voice transcript debug control"
      style={inlineStyle}
    >
      <div
        className="br-voice-debug-drag-handle"
        data-testid="br-voice-debug-drag-handle"
        onMouseDown={onHandleMouseDown}
        onDoubleClick={onHandleDoubleClick}
        title="Drag to move · double-click to reset to default anchor"
        role="button"
        tabIndex={-1}
      >
        <span className="br-voice-debug-grip" aria-hidden="true">⋮⋮</span>
        <label className="br-voice-debug-label" htmlFor="br-voice-debug-textarea">
          voice debug
        </label>
      </div>
      <textarea
        id="br-voice-debug-textarea"
        data-testid="br-voice-debug-textarea"
        className="br-voice-debug-textarea"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          // Ctrl/Cmd+Enter submits (textarea Enter inserts newline).
          if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
            e.preventDefault();
            submit();
          }
        }}
        placeholder='e.g. "Roll 2d6 for damage."'
        rows={2}
      />
      <button
        type="button"
        data-testid="br-voice-debug-send"
        className="br-voice-debug-send"
        onClick={submit}
        disabled={value.trim().length === 0}
      >
        Send
      </button>
    </aside>
  );
}

export default VoiceDebugControl;
